import { FaroToastProps } from "@context-providers/toast/faro-toast-types";
import { useToast } from "@hooks/use-toast";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { AddMembersToTeamResult } from "@store/teams/teams-slice-types";
import { useCallback } from "react";

interface ReturnProps {
  /** Function to respond to user when adding members to company/project/group */
  respondToUser: (
    response:
      | FaroCoreApiTypes.AddMembersToCompanyResponse
      | FaroCoreApiTypes.AddMembersToProjectResponse
      | FaroCoreApiTypes.AddMembersToGroupResponse,
    numberOfMembers: number
  ) => void;

  /** Function to respond to user when adding members to team */
  respondToTeamInvite: (
    response: AddMembersToTeamResult,
    numberOfMembers: number
  ) => void;
}

/** This hook returns functions that checks the backend response and show related toast message based on it */
export function useRespondToInvite(): ReturnProps {
  const { showToast } = useToast();

  const respondToUser = useCallback(
    (
      response:
        | FaroCoreApiTypes.AddMembersToCompanyResponse
        | FaroCoreApiTypes.AddMembersToProjectResponse
        | FaroCoreApiTypes.AddMembersToGroupResponse,
      numberOfMembers: number
    ) => {
      const responseMessage = response.data.message;
      const additionalMessages: string[] = [];
      if (response.errorData) {
        response.errorData.details.failedInvitations?.forEach((error) => {
          additionalMessages.push(error.message);
        });

        response.errorData.details.failedPermissions?.forEach((error) => {
          additionalMessages.push(error.message);
        });
      }

      if (additionalMessages.length === 0) {
        showToast({
          type: "success",
          message: "Invitations sent",
          description: additionalMessages,
        });
      } else if (additionalMessages.length < numberOfMembers) {
        showToast({
          type: "warning",
          message: responseMessage,
          description: additionalMessages.map((message, index) => (
            <li key={index}>{message}</li>
          )),
        });
      } else if (additionalMessages.length === numberOfMembers) {
        showToast({
          type: "error",
          message: responseMessage,
          description: additionalMessages.map((message, index) => (
            <li key={index}>{message}</li>
          )),
        });
      } else {
        throw new Error(
          "Number of additional messages cannot be larger than the actual requests"
        );
      }
    },
    [showToast]
  );

  const respondToTeamInvite = useCallback(
    (response: AddMembersToTeamResult, numberOfMembers: number) => {
      // Show response messages after adding members
      // Errors of creating team is handled by error slice
      const additionalMessages: string[] = [];
      if (response.errorData) {
        response.errorData.forEach((error) => {
          additionalMessages.push(error.message);
        });
      }

      /**
       * Type of toast based on the response status
       * - success: When all the invites go fine
       * - warning: When some of the invites go fine and some are failed
       * - error: When all the invites are failed
       */
      let toastType: FaroToastProps["type"] = "success";
      if (additionalMessages.length > 0) {
        toastType =
          additionalMessages.length < numberOfMembers ? "warning" : "error";
      }

      showToast({
        type: toastType,
        message: "Invitations sent",
        description: additionalMessages.map((message, index) => (
          <li key={index}>
            <var>{message}</var>
          </li>
        )),
      });
    },
    [showToast]
  );

  return { respondToUser, respondToTeamInvite };
}
